import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wallet, Search, RefreshCw, Layers } from 'lucide-react';
import { fetchContributionsLedger } from './hooks/hooks';

const NAV = '#001f3f';
const TEAL = '#0d9488';

const StatCard = ({ icon: Icon, label, value, accent, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
         style={{ backgroundColor: accent + '18' }}>
      <Icon size={22} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Badge = ({ children, color = 'slate' }) => {
  const map = {
    teal:  'bg-teal-100 text-teal-700 border-teal-200',
    blue:  'bg-blue-100 text-blue-700 border-blue-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[color]}`}>
      {children}
    </span>
  );
};

const fmtMoney = (n) => `TZS ${Number(n ?? 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function ContributionsLedger() {
  const [search, setSearch] = useState('');
  const [sourceApp, setSourceApp] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contributions-ledger', sourceApp],
    queryFn: () => fetchContributionsLedger(sourceApp ? { source_app: sourceApp } : {}),
  });

  const records = (data?.data || []).filter(r =>
    !search ||
    r.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    r.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.group_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Contributions Ledger</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Every base amount &amp; compulsory contribution collected into the JamboRicG Wallet, across all Kileleni and Mbeleko groups
          </p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Wallet} label="Total Collected" value={fmtMoney(data?.total_amount)} accent={NAV} sub={`${data?.total_count ?? 0} transactions`} />
        <StatCard icon={Layers} label="Kileleni" value={fmtMoney(data?.by_module?.kileleni)} accent={TEAL} />
        <StatCard icon={Layers} label="Mbeleko" value={fmtMoney(data?.by_module?.mbeleko)} accent="#6366f1" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email, or group…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <select
          value={sourceApp}
          onChange={e => setSourceApp(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">All modules</option>
          <option value="kileleni">Kileleni</option>
          <option value="mbeleko">Mbeleko</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['User', 'Module', 'Group', 'Description', 'Amount', 'Status', 'Date'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map(r => (
                <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{r.user_name || '—'}</div>
                    <div className="text-xs text-slate-400">{r.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={r.source_app === 'kileleni' ? 'teal' : 'blue'}>{r.source_app}</Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.group_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.description}</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{fmtMoney(r.amount)}</td>
                  <td className="px-4 py-3"><Badge color="slate">{r.status}</Badge></td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(r.timestamp)}</td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No contributions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
